// Week.tsx — экран «Неделя» (Ф4). Сетка сдачи отчётов: строка = респондент ×
// анкета, колонка = день периода (из /api/week). Ячейка — глиф статуса; итог —
// «Сдано N/M». Веб-аналог TG-команды /week, но визуально.

import { api } from '../api/client';
import type { WeekResponse } from '../api/contract';
import { StateCard } from '../components/StateBlock';
import { dayHeader } from '../lib/format';
import { CELL_GLYPH, STATUS_LABEL, statusKey } from '../lib/status';
import { useApi } from '../lib/useApi';

/** Цвет итоговой дроби «Сдано»: всё сдано → зелёный, частично → жёлтый. */
function tallyColor(completed: number, scheduled: number): string {
  if (scheduled === 0) return 'var(--muted)';
  return completed >= scheduled ? 'var(--green)' : 'var(--yellow)';
}

export default function Week() {
  const { data, loading, error } = useApi<WeekResponse>(() => api.week(), []);

  return (
    <section>
      <div className="ph">
        <h1>Неделя</h1>
      </div>
      <p className="sub">Сетка сдачи отчётов — кто пропускает (визуальный аналог /week).</p>

      {loading && <StateCard kind="loading" message="Загрузка…" />}
      {error && <StateCard kind="error" message={error} />}

      {data && data.respondents.length === 0 && (
        <StateCard kind="empty" message="За период нет назначенных отчётов." />
      )}

      {data && data.respondents.length > 0 && (
        <div className="card">
          <table className="wk">
            <thead>
              <tr>
                <th className="name" style={{ textAlign: 'left' }}>
                  Сотрудник
                </th>
                {data.days.map((d) => (
                  <th key={d}>{dayHeader(d)}</th>
                ))}
                <th>Сдано</th>
              </tr>
            </thead>
            <tbody>
              {data.respondents.flatMap((r) =>
                r.surveys.map((s) => (
                  <tr key={`${r.respondentId}:${s.surveyCode}`}>
                    <td className="name">
                      {r.name}
                      {r.surveys.length > 1 && (
                        <small style={{ display: 'block', color: 'var(--muted)', fontWeight: 400 }}>
                          {s.surveyCode}
                        </small>
                      )}
                    </td>
                    {s.statuses.map((status, i) => {
                      const key = statusKey(status);
                      return (
                        <td key={data.days[i]}>
                          <span className={`cell ${key}`}>{CELL_GLYPH[key]}</span>
                        </td>
                      );
                    })}
                    <td>
                      <b style={{ color: tallyColor(s.completed, s.scheduled) }}>
                        {s.completed}/{s.scheduled}
                      </b>
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
          <div className="legend">
            <span>
              <span className="cell done" style={{ width: 18, height: 18 }}>
                {CELL_GLYPH.done}
              </span>{' '}
              {STATUS_LABEL.done}
            </span>
            <span>
              <span className="cell prog" style={{ width: 18, height: 18 }}>
                {CELL_GLYPH.prog}
              </span>{' '}
              {STATUS_LABEL.prog}
            </span>
            <span>
              <span className="cell aband" style={{ width: 18, height: 18 }}>
                {CELL_GLYPH.aband}
              </span>{' '}
              {STATUS_LABEL.aband}
            </span>
            <span>
              <span className="cell pend" style={{ width: 18, height: 18 }}>
                {CELL_GLYPH.pend}
              </span>{' '}
              {STATUS_LABEL.pend}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
